import { observer } from "mobx-react";
import { Dialog } from "@/components-v2/Dialog.tsx";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box.tsx";
import { MSelect, Select } from "@/components-v2/Select.tsx";
import { MTextInput, TextInput } from "@/components-v2/TextInput.tsx";
import { PhoneInput } from "@/components-v2/PhoneInput.tsx";
import { MDateInput } from "@/components-v2/DateInput.tsx";
import { when } from "@/utils/functions.ts";
import { useTranslation } from "@/stores/RootStoreContext.ts";
import {
  RoleCustomFields,
  RoleForm,
  RolesDialogsProps,
  RolesDialogState,
} from "@/companyRoles/companyRolesDialogsDomain.ts";
import { makeCountrySelect } from "@/utils/countries.ts";
import { EntityValueFields } from "@/customFields/customFieldsComponents.tsx";

export const RolesDialogs = observer(
  ({
    settingsDialog,
    isCompanyRoleLoading,
    onCloseDialog,
    onSubmitDialog,
    companyName,
  }: RolesDialogsProps) => {
    const { t } = useTranslation();

    return (
      <>
        {(settingsDialog.state === RolesDialogState.CREATE ||
          settingsDialog.state === RolesDialogState.EDIT) && (
          <Dialog
            title={
              settingsDialog.state === RolesDialogState.CREATE
                ? t("settings.createRole")
                : t("settings.editRole", { companyName })
            }
            maxWidth={"sm"}
            isSubmitting={isCompanyRoleLoading}
            onSubmit={onSubmitDialog}
            onCancel={onCloseDialog}
          >
            <VBox gap={"lg"}>
              <CompanyRoleForm form={settingsDialog.form} showPeriod={settingsDialog.showPeriod} />
              {settingsDialog.showResidentialAddress && (
                <ResidentialAddressForm form={settingsDialog.form} />
              )}
              {settingsDialog.customFields && (
                <CompanyRoleCustomFields {...settingsDialog.customFields} />
              )}
            </VBox>
          </Dialog>
        )}
        {settingsDialog.state === RolesDialogState.REMOVE && (
          <Dialog
            title={t("settings.deleteRole")}
            submitLabel={t("confirm")}
            isSubmitting={isCompanyRoleLoading}
            onSubmit={onSubmitDialog}
            onCancel={onCloseDialog}
          >
            {t("settings.deleteRoleQuestion")}
          </Dialog>
        )}
      </>
    );
  },
);

const CompanyRoleCustomFields = observer(({ title, models }: RoleCustomFields) => {
  const { t } = useTranslation();
  if (models.length === 0) {
    return null;
  }
  return (
    <VBox gap={"md"}>
      <Box fontWeight={"bold"}>{t(title)}</Box>
      <Grid gap={"md"}>
        {models.map((model) => (
          <GridItem xs={12} key={model.field.definitionId}>
            <EntityValueFields model={model} label={model.field.name} />
          </GridItem>
        ))}
      </Grid>
    </VBox>
  );
});

const CompanyRoleForm = observer(
  ({ form, showPeriod }: { form: RoleForm; showPeriod?: boolean }) => {
    const { t } = useTranslation();

    return (
      <VBox gap={"md"}>
        <Box fontWeight={"bold"}>{t("settings.systemFields")}</Box>
        <Grid gap={"md"}>
          <GridItem xs={12}>
            <MSelect
              label={t("settings.role")}
              placeholder={t("settings.selectRole")}
              model={form.roleTypeSelection}
              fullWidth
            />
          </GridItem>
          {showPeriod && (
            <>
              <GridItem xs={6}>
                <MDateInput label={t("company-roles.period.from")} model={form.validFrom} />
              </GridItem>
              <GridItem xs={6}>
                <MDateInput
                  label={t({
                    key: "common.label-optional",
                    params: { label: { key: "company-roles.period.to" } },
                  })}
                  model={form.validTo}
                />
              </GridItem>
            </>
          )}
          <GridItem xs={12}>
            <TextInput
              autofocus
              label={t("newShareholder.name")}
              value={form.name.inputValue}
              onChange={form.name.setInputValue}
              onBlur={form.name.commit}
              error={!form.name.isValid}
              message={when(form.name.error, t)}
            />
          </GridItem>
          <GridItem xs={6}>
            <Select
              {...makeCountrySelect({
                label: t("settings.nationality"),
                placeholder: t("not-set"),
                value: form.nationality.inputValue,
                onSelect: form.setNationality,
                fullWidth: true,
              })}
            />
          </GridItem>
          <GridItem xs={6}>
            <TextInput
              value={form.displayedSocialSecurityNumber}
              label={t("settings.socialSecurityNumber")}
              error={!form.socialSecurityNumber.isValid}
              message={when(form.socialSecurityNumber.error, t)}
              onChange={form.socialSecurityNumber.setInputValue}
              onBlur={form.socialSecurityNumber.commit}
              disabled={!form.canEditSocialSecurityNumber}
            />
          </GridItem>
          {form.showDNumber && (
            <GridItem xs={12}>
              <MTextInput label={t("settings.dNumber")} model={form.dNumber} />
            </GridItem>
          )}
          <GridItem xs={6}>
            <MTextInput label={t("settings.email")} model={form.email} />
          </GridItem>
          <GridItem xs={6}>
            <PhoneInput name="phoneNumber" model={form.phoneNumber} />
          </GridItem>
        </Grid>
      </VBox>
    );
  },
);

const ResidentialAddressForm = observer(({ form }: { form: RoleForm }) => {
  const { t } = useTranslation();

  return (
    <VBox gap={"md"}>
      <Box fontWeight={"bold"}>{t("company-roles.residential-address.title")}</Box>
      <Grid gap={"md"}>
        <GridItem xs={12}>
          <Select
            {...makeCountrySelect({
              label: t("entity-data.address-part-country"),
              placeholder: t("not-set"),
              value: form.addressCountry.inputValue,
              onSelect: form.setAddressCountry,
              fullWidth: true,
            })}
          />
        </GridItem>
        <GridItem xs={12}>
          <MTextInput label={t("entity-data.address-part-line1")} model={form.addressLine1} />
        </GridItem>
        <GridItem xs={12}>
          <MTextInput
            label={t({
              key: "common.label-optional",
              params: { label: { key: "entity-data.address-part-line2" } },
            })}
            model={form.addressLine2}
          />
        </GridItem>
        <GridItem xs={6}>
          <MTextInput label={t("entity-data.address-part-postal-code")} model={form.postalCode} />
        </GridItem>
        <GridItem xs={6}>
          <MTextInput label={t("company-roles.residential-address.city")} model={form.city} />
        </GridItem>
      </Grid>
    </VBox>
  );
});
